import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Download, Loader2, ChevronDown, FileText } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

const OEMS = ["FS Curtis", "Becker Mining Systems", "Kito", "Flexitallic", "RAD Torque Systems", "SFE"];

const Kpi = ({ k, label, value, sub }) => (
  <div className="rounded-xl border border-border bg-white p-4" data-testid={`report-kpi-${k}`}>
    <div className="text-[11px] font-semibold text-slate-500">{label}</div>
    <div className="mt-1 font-heading text-3xl font-bold text-[#12294B]">{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
  </div>
);

const Bars = ({ title, rows, total, testid, tone = "#1B4383" }) => (
  <div className="rounded-xl border border-border bg-white p-4" data-testid={testid}>
    <h4 className="mb-3 font-heading text-sm font-bold text-slate-800">{title}</h4>
    {rows.length ? rows.map((r) => (
      <div key={r.name} className="mb-2">
        <div className="flex justify-between text-xs"><span className="font-medium text-slate-700">{r.name}</span><span className="font-mono text-slate-500">{r.count} · {Math.round((r.count * 100) / (total || 1))}%</span></div>
        <div className="mt-1 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full" style={{ width: `${(r.count * 100) / (total || 1)}%`, background: tone }} /></div>
      </div>
    )) : <p className="text-xs text-muted-foreground">No Data Yet.</p>}
  </div>
);

export const EventReport = ({ event }) => {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setR(null); api.get(`/events/${event.id}/report`).then((x) => setR(x.data)).catch((e) => toast.error(apiError(e))); }, [event.id]);
  const pdf = async (brand = "") => {
    setBusy(true);
    try {
      const res = await api.get(`/events/${event.id}/report/pdf`, { params: brand ? { brand } : {}, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const tag = brand ? `-${brand.replace(/[^A-Za-z0-9]+/g, "-")}` : "";
      const a = document.createElement("a"); a.href = url; a.download = `Somcore-Event-Report-${event.code}${tag}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (!r) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  const pct = (n) => `${Math.round((n * 100) / (r.total || 1))}% Of Visitors`;
  return (
    <div className="space-y-4" data-testid="event-report">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-sm font-bold text-slate-800">Event Summary</h3>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" disabled={busy} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="report-download-pdf">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}Download PDF<ChevronDown className="ml-1 h-3.5 w-3.5 opacity-70" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuItem onClick={() => pdf()} data-testid="report-pdf-full"><FileText className="mr-2 h-4 w-4" />Full Event Report</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Per OEM — To Send To Each Partner</DropdownMenuLabel>
            {OEMS.map((b) => {
              const n = (r.brands.find((x) => x.name === b) || {}).count || 0;
              return <DropdownMenuItem key={b} onClick={() => pdf(b)} data-testid={`report-pdf-${b.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}`}><span className="flex-1">{b}</span><span className="font-mono text-[11px] text-muted-foreground">{n}</span></DropdownMenuItem>;
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi k="visitors" label="Visitors" value={r.total} sub={`${r.qr} Scanned QR · ${r.staff} Quick Add`} />
        <Kpi k="clients" label="Became Clients" value={r.clients} sub={pct(r.clients)} />
        <Kpi k="suppliers" label="Became Suppliers" value={r.suppliers} sub={pct(r.suppliers)} />
        <Kpi k="opportunities" label="Opportunities" value={r.opportunities} sub={pct(r.opportunities)} />
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <Bars title="Top Brands Of Interest" rows={r.brands} total={r.total} testid="report-brands" tone="#A87F31" />
        <Bars title="Who Visited" rows={r.types} total={r.total} testid="report-types" />
        <Bars title="Follow-Up Progress" rows={r.follow_up} total={r.total} testid="report-follow-up" tone="#059669" />
        <Bars title="Visitors By Day" rows={r.by_day.map((d) => ({ name: d.day, count: d.count }))} total={r.total} testid="report-days" tone="#64748B" />
      </div>
      <p className="text-xs text-muted-foreground" data-testid="report-converted">{r.converted_any} Of {r.total} Visitors Were Logged Into The CRM · {r.emailed} Thank-You Emails Sent</p>
    </div>
  );
};
